/**
 * The rest of what the Workers page holds, in this screen's plain rows: worker groups (plans that
 * depend on each other), watchers and scans (things looked at on a schedule, that only speak when
 * something is there), and the journal (what the closed trades came to — and what they would have
 * come to with one choice changed).
 */
import { useEffect, useState, type ReactNode } from 'react'
import { ChevronRight, FlaskConical, MessagesSquare, Pause, Pencil, Play, RotateCw, Square, Trash2, Zap } from 'lucide-react'
import type { GroupRecord } from '@shared/groups'
import type { ExitKind, JournalStats, JournalTrade, TradeExcursion, WhatIfSummary } from '@shared/journal'
import { isScan, type WatcherRecord } from '@shared/watch'
import type { WorkerRecord } from '@shared/worker'
import { useWatcher, useWatchers } from '../hooks/useWatcher'
import { useWorkerGroups } from '../hooks/useWorker'
import { watchersApi, workersApi } from './api'
import { Btn, Glyph, Status, askSure, cx, toneOf, usd } from './kit'
import { ask, qtyText } from './Page'
import { useDemo } from './store'
import { workerState } from './Work'
import { starts } from './words'

const money = (n: number) => `${n > 0 ? '+' : n < 0 ? '−' : ''}${usd(Math.abs(n))}`
const head = 'flex h-10 items-center gap-3 border-b border-ws-edge text-ws-fg'
const quiet = 'flex h-7 shrink-0 items-center gap-[6px] rounded-[6px] px-2 text-[12px] tracking-normal text-ws-fg-2 transition-colors duration-150 hover:bg-ws-hover hover:text-ws-fg'
const say = (m: string) => useDemo.getState().say(m)
const fail = (e: unknown) => say(e instanceof Error ? e.message : 'That did not work')
const day = (ms: number) => new Date(ms).toLocaleDateString('en-GB', { day: 'numeric', month: 'short' })
const at = (ms: number) => new Date(ms).toLocaleString('en-GB', { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' })
/** The agent a conversation belongs to (the first agent keeps the ones from before agents). */
const useAgentOf = () => {
  const agents = useDemo((s) => s.agents)
  const sessions = useDemo((s) => s.sessions)
  return (chatId: string | null) => (chatId ? (agents.find((a) => a.id === sessions.find((x) => x.id === chatId)?.agentId) ?? agents.find((a) => a.kind === 'lead')) : agents.find((a) => a.kind === 'lead'))
}

export const Fig = ({ label, value, sub, cls = 'text-ws-fg' }: { label: string; value: ReactNode; sub?: string; cls?: string }) => (
  <div className="px-3 py-3 first:pl-0 max-sm:odd:pl-0">
    <dt className="text-[12px] leading-4 tracking-normal text-ws-fg-3">{label}</dt>
    <dd className={cx('m-0 mt-1 text-[18px] font-medium leading-6 tracking-[-0.015em] tabular-nums', cls)}>{value}</dd>
    {sub && <dd className="m-0 mt-px truncate text-[12px] leading-4 tracking-normal text-ws-fg-4">{sub}</dd>}
  </div>
)
export const Figures = ({ children, cols = 'sm:grid-cols-4' }: { children: ReactNode; cols?: string }) => <dl className={cx('m-0 grid grid-cols-2 border-y border-ws-edge sm:divide-x sm:divide-ws-edge', cols)}>{children}</dl>

/* ── worker groups ───────────────────────────────────────────────────── */

const GSTATE: Record<GroupRecord['status'], { tone: 'free' | 'warn' | 'off' | 'down'; label: string }> = { draft: { tone: 'warn', label: 'Awaiting approval' }, active: { tone: 'free', label: 'Running' }, done: { tone: 'off', label: 'Finished' }, cancelled: { tone: 'off', label: 'Stopped' }, rejected: { tone: 'off', label: 'Rejected' }, expired: { tone: 'off', label: 'Expired' }, error: { tone: 'down', label: 'Needs you' } }

export function GroupsTab({ workers }: { workers: WorkerRecord[] }) {
  const { groups } = useWorkerGroups()
  const setS = useDemo((s) => s.set)
  if (!groups) return <div className="h-24 animate-ws-pulse rounded-[10px] bg-ws-panel" />
  if (!groups.length) return <p className="m-0 mt-5 text-[12px] tracking-normal text-ws-fg-3">No worker groups yet. A group is several plans approved together: one cancels the others, or they share rules.</p>
  return (
    <div className="space-y-10">
      {groups.map((g) => {
        const members = g.summary.members
        const fired = new Set(g.runtime?.firedRules ?? [])
        const realized = g.spec.members.reduce((a, m) => a + (workers.find((w) => w.id === m.workerId)?.runtime?.realized ?? 0), 0)
        const events = [...(g.runtime?.events ?? [])].sort((a, b) => b.at - a.at)
        return (
          <section key={g.id}>
            <div className="flex min-h-7 flex-wrap items-center gap-x-3 gap-y-1">
              <h2 className="m-0 text-[20px] font-medium leading-7 tracking-[-0.015em] text-ws-fg">{g.name}</h2>
              <Status tone={GSTATE[g.status].tone}>{GSTATE[g.status].label}</Status>
              {g.status === 'draft' && (
                <span className="ml-auto flex gap-2">
                  <Btn variant="outline" className="h-7 text-[13px]" onClick={() => askSure({ title: `Reject ${g.name}?`, body: 'The whole group is dropped. Nothing was sent to a broker.', cta: 'Reject', done: () => void workersApi.rejectGroup(g.id).catch(fail) })}>
                    Reject
                  </Btn>
                  <Btn variant="white" className="h-7 text-[13px]" title="Approval is bound to exactly these plans" onClick={() => void workersApi.approveGroup(g.id, g.planHash).then(() => say('Approved — the group is running'), fail)}>
                    Approve
                  </Btn>
                </span>
              )}
              {(g.status === 'active' || g.status === 'error') && (
                <Btn variant="danger" className="ml-auto h-7 text-[13px]" onClick={() => askSure({ title: `Stop ${g.name}?`, body: 'Every worker of the group is stopped: its orders are cancelled and what it holds is sold at the market.', cta: 'Stop group', done: () => void workersApi.closeGroup(g.id).then(() => say(`${g.name} is stopping`), fail) })}>
                  Stop group
                </Btn>
              )}
            </div>
            <div className="mt-4">
              <Figures>
                <Fig label="Max loss · after fees" value={money(-g.summary.maxLoss)} sub={g.spec.oneCancelsOthers ? 'worst single member' : 'every member at its stops'} cls="text-ws-down" />
                <Fig label="Members" value={members.length} sub={members.map((m) => m.symbol).join(' · ')} />
                <Fig label="Linked by" value={g.spec.oneCancelsOthers ? 'One of' : g.spec.rules.length ? 'Rules' : 'Start order'} sub={`${g.spec.rules.length} group ${g.spec.rules.length === 1 ? 'rule' : 'rules'}`} />
                <Fig label="Realized" value={money(realized)} cls={realized ? toneOf(realized) : 'text-ws-fg'} />
              </Figures>
            </div>
            <p className="m-0 mt-3 text-[12px] leading-[17px] tracking-normal text-ws-fg-3">{g.summary.maxLossText}</p>
            {(g.error || g.summary.warnings.length > 0) && (
              <ul className="m-0 mt-2 list-none p-0">
                {[...(g.error ? [g.error] : []), ...g.summary.warnings].map((x) => (
                  <li key={x} className="flex gap-3 py-1 text-[12px] leading-[17px] tracking-normal text-ws-fg-2">
                    <span className="mt-[6px] block h-[6px] w-[6px] shrink-0 rounded-full bg-ws-warn" />
                    {x}
                  </li>
                ))}
              </ul>
            )}

            <div className="mt-6 grid gap-x-8 gap-y-8 lg:grid-cols-2">
              <div>
                <div className={head}>Members</div>
                <div className="divide-y divide-ws-edge">
                  {members.map((m) => {
                    const w = workers.find((x) => x.id === m.workerId)
                    return (
                      <button key={m.key} type="button" onClick={() => setS({ workerId: m.workerId })} className="-mx-2 flex min-h-11 w-[calc(100%+16px)] items-center gap-3 rounded-[6px] px-2 py-[6px] text-left text-[13px] tracking-normal transition-colors duration-150 hover:bg-ws-hover">
                        <span className="min-w-0 flex-1">
                          <span className="block truncate text-[14px] tracking-[-0.01em] text-ws-fg">{m.name}</span>
                          <span className="block truncate text-[12px] leading-4 text-ws-fg-3">
                            {m.symbol} · {m.startText ?? m.headline}
                            {g.runtime?.winner === m.key ? ' · filled first' : ''}
                          </span>
                        </span>
                        {w && <Status tone={workerState(w).tone}>{workerState(w).label}</Status>}
                      </button>
                    )
                  })}
                </div>
                <div className={cx(head, 'mt-6')}>How they work together</div>
                <ul className="m-0 list-none divide-y divide-ws-edge p-0">
                  {g.summary.behaviour.map((b) => (
                    <li key={b} className="py-[10px] text-[13px] leading-5 tracking-normal text-ws-fg-2">
                      {b}
                    </li>
                  ))}
                </ul>
              </div>
              <div>
                <div className={head}>Group rules</div>
                <ul className="m-0 list-none divide-y divide-ws-edge p-0">
                  {g.summary.rulesText.map((r) => (
                    <li key={r.id} className="flex gap-3 py-[10px] text-[13px] leading-5 tracking-normal text-ws-fg-2">
                      <span className={cx('mt-[7px] block h-[6px] w-[6px] shrink-0 rounded-full', fired.has(r.id) ? 'bg-ws-up' : 'bg-ws-fg-5')} />
                      <span>
                        {r.text}
                        {fired.has(r.id) && <span className="ml-2 text-[12px] text-ws-fg-4">fired</span>}
                      </span>
                    </li>
                  ))}
                  {!g.summary.rulesText.length && <li className="py-[10px] text-[13px] leading-5 tracking-normal text-ws-fg-4">No rules of its own.</li>}
                </ul>
                <div className={cx(head, 'mt-6')}>Group timeline</div>
                <ol className="m-0 list-none divide-y divide-ws-edge p-0">
                  {events.map((e, i) => (
                    <li key={i} className="py-[10px] text-[13px] leading-5 tracking-normal">
                      <span className="block text-ws-fg-2">{e.message}</span>
                      <span className="block text-[12px] leading-4 text-ws-fg-4">{at(e.at)}</span>
                    </li>
                  ))}
                  {!events.length && <li className="py-[10px] text-[13px] leading-5 tracking-normal text-ws-fg-4">{g.approvedAt ? `Approved ${at(g.approvedAt)}` : `Drafted ${at(g.createdAt)}`}</li>}
                </ol>
              </div>
            </div>
            {g.spec.note && (
              <>
                <div className={cx(head, 'mt-6')}>Why</div>
                <p className="m-0 mt-3 max-w-[720px] whitespace-pre-line text-[13px] leading-[20px] tracking-normal text-ws-fg-2">{g.spec.note}</p>
              </>
            )}
          </section>
        )
      })}
    </div>
  )
}

/* ── watchers and scans ──────────────────────────────────────────────── */

const wstate = (w: WatcherRecord): { tone: 'free' | 'off'; label: string } => (w.status === 'active' ? { tone: 'free', label: 'Active' } : w.status === 'paused' ? { tone: 'off', label: 'Paused' } : { tone: 'off', label: 'Ended' })
/** What it looks at, before its details are read. */
const about = (w: WatcherRecord) => {
  if (isScan(w.spec)) {
    const u = w.spec.universe
    const where = u.kind === 'list' ? (u.preset ? { sp500: 'S&P 500', nasdaq100: 'Nasdaq-100', dow30: 'Dow 30', watchlist: 'your watchlist', portfolio: 'your holdings' }[u.preset] : `${u.symbols.length} stocks`) : 'a screen of the market'
    return `Looks through ${where}${w.nextRunAt ? ` · next ${at(w.nextRunAt)}` : ''}`
  }
  return `${w.spec.symbol} · ${w.spec.rules.length} ${w.spec.rules.length === 1 ? 'rule' : 'rules'}${w.endsAt ? ` · until ${day(w.endsAt)}` : ''}`
}

/** A watcher opened: its rules in words with what each sees now, what it found, and what can be done. */
function WatcherBody({ w }: { w: WatcherRecord }) {
  const { watcher } = useWatcher(w.id)
  const agentOf = useAgentOf()
  const openChat = useDemo((s) => s.openChat)
  const by = agentOf(w.chatId)
  const hits = w.runtime.lastHits ?? []
  const set = (status: WatcherRecord['status'], done: string) => void watchersApi.setStatus(w.id, status).then(() => say(done), fail)
  return (
    <div className="border-t border-ws-hair bg-ws-well px-3 pb-2 pl-[30px] pt-3">
      {watcher && (
        <div className="text-[12px] leading-4 tracking-normal text-ws-fg-4">
          {watcher.scheduleText ?? watcher.hoursText}
          {watcher.checkingNow ? ' · checking now' : ''}
          {watcher.quote?.price != null ? ` · ${isScan(w.spec) ? '' : w.spec.symbol} ${watcher.quote.price.toFixed(2)}` : ''}
        </div>
      )}
      {watcher && watcher.rules.length > 0 && (
        <ul className="m-0 mt-2 list-none p-0">
          {watcher.rules.map((r) => (
            <li key={r.id} className="flex gap-3 py-1 text-[13px] leading-5 tracking-normal">
              <span className={cx('mt-[7px] block h-[6px] w-[6px] shrink-0 rounded-full', r.done ? 'bg-ws-fg-5' : r.holds ? 'bg-ws-up' : 'bg-ws-fg-4')} />
              <span className="min-w-0">
                <span className="block text-ws-fg-2">{r.text}</span>
                <span className="block text-[12px] leading-4 text-ws-fg-4">
                  {r.now ?? 'no reading yet'}
                  {r.fires ? ` · spoke ${r.fires} ${r.fires === 1 ? 'time' : 'times'}${r.lastAt ? `, last ${at(r.lastAt)}` : ''}` : ''}
                </span>
              </span>
            </li>
          ))}
        </ul>
      )}
      {hits.length > 0 && (
        <>
          <div className="mt-3 text-[12px] leading-4 tracking-normal text-ws-fg-4">Last hits{w.runtime.lastRunAt ? ` · ${at(w.runtime.lastRunAt)}` : ''}</div>
          <div className="mt-2 flex flex-wrap gap-1">
            {hits.map((h) => (
              <button key={h.symbol} type="button" title={h.name ?? undefined} onClick={() => useDemo.getState().openStock(h.symbol)} className="flex h-6 items-center rounded-[6px] bg-ws-panel px-2 text-[12px] font-medium tracking-normal text-ws-fg-2 transition-colors duration-150 hover:text-ws-fg">
                {h.symbol}
                {h.changePct != null && <span className={cx('ml-[6px] font-normal tabular-nums', toneOf(h.changePct))}>{`${h.changePct >= 0 ? '+' : '−'}${Math.abs(h.changePct).toFixed(1)}%`}</span>}
              </button>
            ))}
          </div>
        </>
      )}
      {watcher && watcher.events.length > 0 && (
        <>
          <div className="mt-4 text-[12px] leading-4 tracking-normal text-ws-fg-4">Recent</div>
          <ol className="m-0 mt-1 list-none p-0">
            {watcher.events.slice(0, 6).map((e) => (
              <li key={e.id} className="grid grid-cols-[104px_1fr] gap-3 py-1 text-[13px] leading-5 tracking-normal">
                <span className="text-[12px] text-ws-fg-3">{at(e.createdAt)}</span>
                <span className="text-ws-fg-2">{e.message}</span>
              </li>
            ))}
          </ol>
        </>
      )}
      <div className="-ml-2 mt-2 flex flex-wrap items-center gap-1 border-t border-ws-hair pt-2">
        {w.status !== 'done' ? (
          <>
            <button type="button" onClick={() => set(w.status === 'paused' ? 'active' : 'paused', w.status === 'paused' ? 'It looks again' : 'Paused')} className={quiet}>
              {w.status === 'paused' ? <Play size={13} strokeWidth={2} /> : <Pause size={13} strokeWidth={2} />}
              {w.status === 'paused' ? 'Resume' : 'Pause'}
            </button>
            <button type="button" onClick={() => set('done', 'Ended')} className={quiet}>
              <Square size={12} strokeWidth={2} />
              End
            </button>
            {isScan(w.spec) && (
              <button type="button" onClick={() => void watchersApi.runNow(w.id).then(() => say('Running now — it writes to its conversation if it finds something'), fail)} className={quiet}>
                <Zap size={13} strokeWidth={2} />
                Run now
              </button>
            )}
          </>
        ) : (
          <button type="button" onClick={() => set('active', 'Restarted')} className={quiet}>
            <RotateCw size={13} strokeWidth={2} />
            Restart
          </button>
        )}
        {by && (
          <>
            <button type="button" onClick={() => (w.chatId ? void openChat(w.chatId) : useDemo.getState().openAgent(by.id))} className={quiet}>
              <Glyph palette={by.palette} size={14} />
              {by.name}
            </button>
            <button type="button" onClick={() => ask(by.id, starts.editWatch(w.name, isScan(w.spec)))} className={quiet}>
              <Pencil size={13} strokeWidth={2} />
              Edit in chat
            </button>
          </>
        )}
        <button type="button" onClick={() => askSure({ title: `Delete “${w.name}”?`, body: 'It stops looking and is removed. What it found stays in the conversation it wrote to.', cta: 'Delete', done: () => void watchersApi.remove(w.id).then(() => say('Deleted'), fail) })} className={cx(quiet, 'ml-auto hover:text-ws-down')}>
          <Trash2 size={13} strokeWidth={2} />
          Delete
        </button>
      </div>
    </div>
  )
}

export function WatchersTab() {
  const { watchers } = useWatchers()
  const [open, setOpen] = useState<string | null>(null)
  if (!watchers) return <div className="h-24 animate-ws-pulse rounded-[10px] bg-ws-panel" />
  if (!watchers.length) return <p className="m-0 mt-5 text-[12px] tracking-normal text-ws-fg-3">No watchers or scans. Ask an agent to keep an eye on a price, or to look through the market on a schedule.</p>
  return (
    <div className="-mx-3 divide-y divide-ws-edge border-y border-ws-edge">
      {watchers.map((w) => {
        const on = open === w.id
        return (
          <div key={w.id}>
            <button type="button" aria-expanded={on} onClick={() => setOpen(on ? null : w.id)} className={cx('grid min-h-[52px] w-full grid-cols-[1fr_auto] items-center gap-x-3 px-3 py-2 text-left tracking-normal transition-colors duration-150 hover:bg-ws-hover md:grid-cols-[minmax(0,1fr)_72px_minmax(150px,auto)]', on && 'bg-ws-hover')}>
              <span className="flex min-w-0 items-center gap-2">
                <ChevronRight size={13} strokeWidth={2} className={cx('-ml-1 shrink-0 text-ws-fg-4 transition-transform duration-150', on && 'rotate-90')} />
                <span className="min-w-0">
                  <span className={cx('block truncate text-[14px] leading-[18px] tracking-[-0.01em]', w.status === 'done' ? 'text-ws-fg-3' : 'text-ws-fg')}>{w.name}</span>
                  <span className="block truncate text-[12px] leading-4 text-ws-fg-3">{about(w)}</span>
                </span>
              </span>
              <span className="hidden text-[12px] text-ws-fg-3 md:block">{w.kind === 'scan' ? 'Scan' : 'Watcher'}</span>
              <span className="flex justify-end">
                <Status tone={wstate(w).tone}>{wstate(w).label}</Status>
              </span>
            </button>
            {on && <WatcherBody w={w} />}
          </div>
        )
      })}
    </div>
  )
}

/* ── the journal ─────────────────────────────────────────────────────── */

const EXIT: Record<ExitKind, string> = { target: 'Target', stall: 'Lowered target', stop: 'Stop', raisedStop: 'Raised stop', reduce: 'Part sold', close: 'Closed by a rule', manual: 'Stopped by you', open: 'Open' }

function TradeBody({ t }: { t: JournalTrade }) {
  const [ex, setEx] = useState<TradeExcursion | null>(null)
  const lead = useDemo((s) => s.agents.find((a) => a.kind === 'lead') ?? s.agents[0])
  useEffect(() => {
    if (t.source !== 'worker' || t.closedAt == null) return
    let on = true
    void workersApi.excursion(t.id).then((x) => on && !('error' in x) && setEx(x), () => {})
    return () => {
      on = false
    }
  }, [t.id, t.source, t.closedAt])
  return (
    <div className="border-t border-ws-hair bg-ws-well px-3 pb-3 pl-[30px] pt-2">
      <div className="grid h-7 grid-cols-[36px_minmax(0,1fr)_minmax(0,1.6fr)_80px] items-center gap-3 text-[12px] tracking-normal text-ws-fg-4">
        <span>Lot</span>
        <span>Bought</span>
        <span>How it left</span>
        <span className="text-right">Result</span>
      </div>
      {t.lots.map((l) => {
        const sold = l.exits.filter((e) => e.kind !== 'open')
        const result = sold.length ? sold.reduce((a, e) => a + (e.price - l.entry) * e.shares, 0) : null
        const left = sold.length ? sold.map((e) => `${EXIT[e.kind]} ${e.price.toFixed(2)} · ${day(e.at)}`).join('; ') : `Open · ${l.target != null ? `target ${l.target.toFixed(2)}, ` : ''}stop ${l.stop.toFixed(2)}`
        return (
          <div key={l.lot} className="grid min-h-8 grid-cols-[36px_minmax(0,1fr)_minmax(0,1.6fr)_80px] items-center gap-3 border-t border-ws-hair text-[13px] tracking-normal tabular-nums">
            <span className="text-ws-fg-3">{l.lot}</span>
            <span className="text-ws-fg-2">
              {qtyText(l.qty)} at {l.entry.toFixed(2)}
            </span>
            <span className="text-ws-fg-2">{left}</span>
            <span className={cx('text-right', result == null ? 'text-ws-fg-4' : toneOf(result))}>{result == null ? '—' : money(result)}</span>
          </div>
        )
      })}
      <div className="mt-3 text-[12px] leading-4 tracking-normal text-ws-fg-4">What really happened</div>
      <p className="m-0 mt-1 text-[13px] leading-5 tracking-normal text-ws-fg-2">
        {t.exitNotes.length ? t.exitNotes.join(' ') : t.closedAt == null ? 'Nothing sold yet.' : 'Sold as planned.'}
        {t.fees > 0 ? ` Fees ${usd(t.fees)}.` : ''}
      </p>
      {ex && (ex.mfe || ex.mae) && (
        <p className="m-0 mt-1 text-[12px] leading-[17px] tracking-normal text-ws-fg-3">
          {ex.mfe ? `Best price while held ${ex.mfe.price.toFixed(2)}${ex.mfe.r != null ? ` (${ex.mfe.r.toFixed(2)}R)` : ''}` : ''}
          {ex.mfe && ex.mae ? ' · ' : ''}
          {ex.mae ? `worst ${ex.mae.price.toFixed(2)}${ex.mae.r != null ? ` (${ex.mae.r.toFixed(2)}R)` : ''}` : ''}
          {ex.after5 != null ? ` · 5 sessions after it left: ${ex.after5.toFixed(2)}` : ''}
        </p>
      )}
      {lead && (
        <button type="button" onClick={() => ask(lead.id, starts.learned(t.name))} className={cx(quiet, '-ml-2 mt-2')}>
          <MessagesSquare size={13} strokeWidth={2} />
          Ask {lead.name} about it
        </button>
      )}
    </div>
  )
}

export function JournalTab() {
  const [data, setData] = useState<{ trades: JournalTrade[]; stats: JournalStats } | null>(null)
  const [open, setOpen] = useState<string | null>(null)
  const [what, setWhat] = useState<WhatIfSummary[] | 'busy' | null>(null)
  useEffect(() => {
    void workersApi.journal().then(setData, fail)
  }, [])
  if (!data) return <div className="h-24 animate-ws-pulse rounded-[10px] bg-ws-panel" />
  const s = data.stats
  if (!data.trades.length) return <p className="m-0 mt-5 text-[12px] tracking-normal text-ws-fg-3">No trades yet. Every trade a worker makes is written here once it buys: how each lot left, and what it came to.</p>
  const exits = (Object.entries(s.byExit) as [ExitKind, number][]).filter(([k, n]) => k !== 'open' && n > 0)
  return (
    <div>
      <Figures cols="sm:grid-cols-5">
        <Fig label="Trades" value={`${s.closed} / ${s.trades}`} sub="closed / all" />
        <Fig label="Win rate" value={s.winRate == null ? '—' : `${Math.round(s.winRate * (s.winRate <= 1 ? 100 : 1))}%`} sub={`${s.wins} won`} />
        <Fig label="Net" value={money(s.net)} sub="closed trades, after fees" cls={s.net ? toneOf(s.net) : 'text-ws-fg'} />
        <Fig label="Average R" value={s.avgR == null ? '—' : `${s.avgR.toFixed(2)}R`} sub={s.avgNet == null ? undefined : `${money(s.avgNet)} a trade`} cls={s.avgR ? toneOf(s.avgR) : 'text-ws-fg'} />
        <Fig label="Best · worst" value={s.best == null ? '—' : money(s.best)} sub={s.worst == null ? undefined : `worst ${money(s.worst)}`} cls={s.best ? toneOf(s.best) : 'text-ws-fg'} />
      </Figures>
      <div className="flex min-h-11 flex-wrap items-center justify-between gap-2 py-2 text-[12px] tracking-normal text-ws-fg-3">
        <span>{exits.length ? `Shares by exit: ${exits.map(([k, n]) => `${EXIT[k].toLowerCase()} ${qtyText(n)}`).join(' · ')}` : 'Nothing sold yet'}</span>
        <Btn
          variant="outline"
          icon={FlaskConical}
          iconSize={14}
          className="h-7 text-[13px]"
          disabled={what === 'busy' || !s.closed}
          title="Replays every closed trade on its own 5-minute bars with one choice changed"
          onClick={() => {
            if (Array.isArray(what)) return setWhat(null)
            setWhat('busy')
            void workersApi.whatIf().then((r) => setWhat(r.summary), (e) => (setWhat(null), fail(e)))
          }}
        >
          {what === 'busy' ? 'Replaying' : Array.isArray(what) ? 'Hide what-if' : 'What if (all trades)'}
        </Btn>
      </div>
      {Array.isArray(what) && (
        <div className="mb-6 animate-ws-in">
          <div className={head}>What if</div>
          <div className="divide-y divide-ws-edge">
            <div className="grid h-9 grid-cols-[minmax(0,1fr)_88px_88px_64px] items-center gap-3 text-[13px] tracking-normal tabular-nums">
              <span className="text-ws-fg">As it happened</span>
              <span className={cx('text-right', toneOf(s.net))}>{money(s.net)}</span>
              <span className="text-right text-ws-fg-4">—</span>
              <span className="text-right text-[12px] text-ws-fg-3">
                {s.wins} / {s.closed}
              </span>
            </div>
            {what.map((x) => (
              <div key={x.variant} className="grid h-9 grid-cols-[minmax(0,1fr)_88px_88px_64px] items-center gap-3 text-[13px] tracking-normal tabular-nums">
                <span className="truncate text-ws-fg-2">{x.label}</span>
                <span className={cx('text-right', toneOf(x.net))}>{money(x.net)}</span>
                <span className={cx('text-right', x.delta ? toneOf(x.delta) : 'text-ws-fg-4')}>{x.delta ? money(x.delta) : '—'}</span>
                <span className="text-right text-[12px] text-ws-fg-3">
                  {x.wins} / {x.trades}
                </span>
              </div>
            ))}
            {!what.length && <p className="m-0 py-[10px] text-[13px] tracking-normal text-ws-fg-3">No closed trade could be replayed.</p>}
          </div>
          <p className="m-0 mt-2 text-[12px] leading-[17px] tracking-normal text-ws-fg-4">Same entries, 5-minute bars in regular hours, the worse case inside a bar, the broker’s fees. {s.closed} {s.closed === 1 ? 'trade' : 'trades'} — few trades are weak evidence.</p>
        </div>
      )}
      <div className="-mx-3 divide-y divide-ws-edge border-y border-ws-edge">
        {data.trades.map((t) => {
          const on = open === t.id
          const closed = t.closedAt != null
          return (
            <div key={t.id}>
              <button type="button" aria-expanded={on} onClick={() => setOpen(on ? null : t.id)} className={cx('grid min-h-[52px] w-full grid-cols-[1fr_auto] items-center gap-x-3 px-3 py-2 text-left tracking-normal transition-colors duration-150 hover:bg-ws-hover', on && 'bg-ws-hover')}>
                <span className="flex min-w-0 items-center gap-2">
                  <ChevronRight size={13} strokeWidth={2} className={cx('-ml-1 shrink-0 text-ws-fg-4 transition-transform duration-150', on && 'rotate-90')} />
                  <span className="min-w-0">
                    <span className="block truncate text-[14px] leading-[18px] tracking-[-0.01em] text-ws-fg">{t.name}</span>
                    <span className="block truncate text-[12px] leading-4 text-ws-fg-3">
                      {t.symbol} · {day(t.openedAt)} – {closed ? day(t.closedAt!) : 'still open'}
                    </span>
                  </span>
                </span>
                <span className="text-right tabular-nums">
                  <span className={cx('block text-[14px] leading-[18px]', closed ? toneOf(t.net) : 'text-ws-fg-3')}>{closed ? money(t.net) : 'Open'}</span>
                  {closed && t.r != null && <span className="block text-[12px] leading-4 text-ws-fg-3">{t.r.toFixed(2)}R</span>}
                </span>
              </button>
              {on && <TradeBody t={t} />}
            </div>
          )
        })}
      </div>
    </div>
  )
}
